/* eslint-disable jsdoc/require-returns */
/* eslint-disable jsdoc/require-param-type */

/**
 * LineChart Component
 *
 * A reusable line chart component built with Recharts.
 * Supports single or multi-series data with customizable styling.
 *
 * @example
 * // Single series
 * <LineChart
 *   data={[{ year: 2021, revenue: 210 }, { year: 2022, revenue: 235 }]}
 *   xKey="year"
 *   yKey="revenue"
 *   formatY={(v) => `$${v}B`}
 * />
 *
 * @example
 * // Multi-series with different line styles
 * <LineChart
 *   data={[
 *     { year: 2021, revenue: 210, series: 'historical' },
 *     { year: 2024, revenue: 310, series: 'projected' },
 *   ]}
 *   xKey="year"
 *   yKey="revenue"
 *   seriesKey="series"
 *   seriesConfig={{
 *     historical: { color: '#2563EB', strokeStyle: 'solid', label: 'Historical' },
 *     projected: { color: '#93C5FD', strokeStyle: 'dashed', label: 'Projected' }
 *   }}
 * />
 */

import { useMemo } from 'react';

import PropTypes from 'prop-types';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';

import * as Styled from '../Charts.style';
import { getChartColors, getSeriesColor } from '../utils/colors';
import { identity } from '../utils/formatters';

/**
 * Custom tooltip component
 * @param root0
 * @param root0.active
 * @param root0.payload
 * @param root0.formatTooltip
 * @param root0.xKey
 */
const CustomTooltip = ({ active, payload, formatTooltip, xKey }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const dataPoint = payload[0]?.payload;

  if (!dataPoint) {
    return null;
  }

  // Use custom formatter if provided
  if (formatTooltip) {
    const tooltipData = formatTooltip(dataPoint);

    return (
      <Styled.TooltipContainer>
        {tooltipData.title && (
          <Styled.TooltipTitle size="xs">{tooltipData.title}</Styled.TooltipTitle>
        )}
        {Object.entries(tooltipData)
          .filter(([key]) => key !== 'title' && key !== 'badge')
          .map(([label, value]) => (
            <Styled.TooltipRow key={label}>
              <Text size="xs">{label}:</Text>
              <Text size="xs" weight="medium">
                {value}
              </Text>
            </Styled.TooltipRow>
          ))}
        {tooltipData.badge && <Styled.TooltipBadge>{tooltipData.badge}</Styled.TooltipBadge>}
      </Styled.TooltipContainer>
    );
  }

  // Default tooltip
  return (
    <Styled.TooltipContainer>
      <Styled.TooltipTitle>{dataPoint[xKey]}</Styled.TooltipTitle>
      {payload.map((entry) => (
        <Styled.TooltipRow key={entry.dataKey}>
          <span>{entry.name || entry.dataKey}:</span>
          <strong>{entry.value}</strong>
        </Styled.TooltipRow>
      ))}
    </Styled.TooltipContainer>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  formatTooltip: PropTypes.func,
  xKey: PropTypes.string
};

/**
 * LineChart Component
 * @param root0
 * @param root0.data
 * @param root0.xKey
 * @param root0.yKey
 * @param root0.seriesKey
 * @param root0.seriesConfig
 * @param root0.color
 * @param root0.strokeStyle
 * @param root0.xAxisLabel
 * @param root0.yAxisLabel
 * @param root0.formatX
 * @param root0.formatY
 * @param root0.formatTooltip
 * @param root0.referenceLines
 * @param root0.height
 * @param root0.showLegend
 * @param root0.showGrid
 * @param root0.showDots
 * @param root0.title
 * @param root0.margin
 */
const LineChart = ({
  // Data
  data = [],
  xKey = 'x',
  yKey = 'y',
  seriesKey = null,

  // Series configuration (for multi-line charts)
  seriesConfig = {},

  // Single series styling (when no seriesKey)
  color = null,
  strokeStyle = 'solid',

  // Axis configuration
  xAxisLabel = '',
  yAxisLabel = '',
  formatX = identity,
  formatY = identity,

  // Tooltip
  formatTooltip = null, // eslint-disable-line no-unused-vars

  // Reference lines
  referenceLines = [],

  // Layout
  height = 300,
  showLegend = true,
  showGrid = true,
  showDots = true,
  title = '',

  // Margins
  margin = { top: 20, right: 30, left: 20, bottom: 20 }
}) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const defaultColor = color || chartColors.primary;
  // Process data for multi-series if seriesKey is provided
  const { chartData, seriesNames } = useMemo(() => {
    if (!seriesKey) {
      return { chartData: data, seriesNames: [] };
    }

    // Get unique series names
    const names = [...new Set(data.map((item) => item[seriesKey]))];

    // For multi-series, we need to restructure data so each row has all series values
    const xValues = [...new Set(data.map((item) => item[xKey]))];
    const restructured = xValues.map((xVal) => {
      const row = { [xKey]: xVal };
      names.forEach((seriesName) => {
        const item = data.find((d) => d[xKey] === xVal && d[seriesKey] === seriesName);
        row[seriesName] = item ? item[yKey] : null;
      });

      return row;
    });

    return { chartData: restructured, seriesNames: names };
  }, [data, seriesKey, xKey, yKey]);

  // Empty state
  if (!data?.length) {
    return (
      <Styled.ChartWrapper>
        {title && <Styled.ChartTitle>{title}</Styled.ChartTitle>}
        <Styled.EmptyState>No data available</Styled.EmptyState>
      </Styled.ChartWrapper>
    );
  }

  // Get line config for a series
  const getLineConfig = (seriesName, index) => {
    const config = seriesConfig[seriesName] || {};

    return {
      stroke: config.color || getSeriesColor(index, theme),
      strokeDasharray: config.strokeStyle === 'dashed' ? '8 4' : undefined,
      name: config.label || seriesName
    };
  };

  // Render legend if multi-series
  const renderLegend = () => {
    if (!showLegend || seriesNames.length === 0) {
      return null;
    }

    return (
      <Styled.LegendContainer>
        {seriesNames.map((name, index) => {
          const config = getLineConfig(name, index);

          return (
            <Styled.LegendItem key={name}>
              <Styled.LegendColor
                style={{
                  backgroundColor: config.stroke,
                  borderStyle: config.strokeDasharray ? 'dashed' : 'solid'
                }}
                variant="line"
              />
              <Styled.LegendText>{config.name}</Styled.LegendText>
            </Styled.LegendItem>
          );
        })}
      </Styled.LegendContainer>
    );
  };

  return (
    <Styled.ChartWrapper>
      {title && <Styled.ChartTitle>{title}</Styled.ChartTitle>}
      <ResponsiveContainer height={height} width="100%">
        <RechartsLineChart data={chartData} margin={margin}>
          {showGrid && <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" />}

          <XAxis
            dataKey={xKey}
            label={
              xAxisLabel
                ? { value: xAxisLabel, position: 'bottom', offset: 0, fontSize: 12 }
                : undefined
            }
            stroke={chartColors.axis}
            tick={{ fontSize: 12 }}
            tickFormatter={formatX}
          />

          <YAxis
            label={
              yAxisLabel
                ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 12 }
                : undefined
            }
            stroke={chartColors.axis}
            tick={{ fontSize: 12 }}
            tickFormatter={formatY}
            width={70}
          />

          {/* <Tooltip content={<CustomTooltip formatTooltip={formatTooltip} xKey={xKey} />} /> */}

          {showLegend && seriesNames.length > 0 && <Legend content={renderLegend} />}

          {/* Reference lines */}
          {/* eslint-disable react/no-array-index-key -- Index combined with ref value for unique keys */}
          {referenceLines.map((ref, index) => (
            <ReferenceLine
              key={`ref-${ref.x || ref.y}-${index}`}
              label={ref.label ? { value: ref.label, position: 'top', fontSize: 10 } : undefined}
              stroke={ref.color || chartColors.axis}
              strokeDasharray="5 5"
              x={ref.x}
              y={ref.y}
            />
          ))}
          {/* eslint-enable react/no-array-index-key */}

          {/* Render lines based on whether multi-series or single */}
          {seriesNames.length > 0 ? (
            // Multi-series lines
            seriesNames.map((seriesName, index) => {
              const config = getLineConfig(seriesName, index);

              return (
                <Line
                  key={seriesName}
                  activeDot={{ r: 7 }}
                  connectNulls={false}
                  dataKey={seriesName}
                  dot={showDots ? { fill: config.stroke, r: 4 } : false}
                  name={config.name}
                  stroke={config.stroke}
                  strokeDasharray={config.strokeDasharray}
                  strokeWidth={2}
                  type="monotone"
                />
              );
            })
          ) : (
            // Single line
            <Line
              activeDot={{ r: 7 }}
              dataKey={yKey}
              dot={showDots ? { fill: defaultColor, r: 4 } : false}
              stroke={defaultColor}
              strokeDasharray={strokeStyle === 'dashed' ? '8 4' : undefined}
              strokeWidth={2}
              type="monotone"
            />
          )}
        </RechartsLineChart>
      </ResponsiveContainer>
    </Styled.ChartWrapper>
  );
};

LineChart.propTypes = {
  /** Array of data objects */
  data: PropTypes.arrayOf(PropTypes.object),
  /** Key for X-axis values */
  xKey: PropTypes.string,
  /** Key for Y-axis values */
  yKey: PropTypes.string,
  /** Key for series differentiation (enables multi-line mode) */
  seriesKey: PropTypes.string,
  /** Configuration for each series: { seriesName: { color, strokeStyle, label } } */
  seriesConfig: PropTypes.objectOf(
    PropTypes.shape({
      color: PropTypes.string,
      strokeStyle: PropTypes.oneOf(['solid', 'dashed']),
      label: PropTypes.string
    })
  ),
  /** Line color (single series mode) */
  color: PropTypes.string,
  /** Line style (single series mode) */
  strokeStyle: PropTypes.oneOf(['solid', 'dashed']),
  /** X-axis label */
  xAxisLabel: PropTypes.string,
  /** Y-axis label */
  yAxisLabel: PropTypes.string,
  /** Formatter for X-axis tick labels */
  formatX: PropTypes.func,
  /** Formatter for Y-axis tick labels */
  formatY: PropTypes.func,
  /** Custom tooltip formatter: (dataPoint) => { title, ...labels, badge } */
  formatTooltip: PropTypes.func,
  /** Reference lines: [{ x, y, label, color }] */
  referenceLines: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      y: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      color: PropTypes.string
    })
  ),
  /** Chart height in pixels */
  height: PropTypes.number,
  /** Show legend */
  showLegend: PropTypes.bool,
  /** Show grid lines */
  showGrid: PropTypes.bool,
  /** Show dots on data points */
  showDots: PropTypes.bool,
  /** Chart title */
  title: PropTypes.string,
  /** Chart margins */
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number
  })
};

export default LineChart;
